import { mapState } from 'vuex';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import { set } from 'lodash';
export default {
  name: 'CoreDtwinStateEditTransformer',
  props: {
    // the current edited state
    lclModel: {
      type: Object,
      required: true
    },
    // is this an edit, create, or readonly?
    mode: {
      type: String,
      required: false,
      default: () => 'edit'
    },
    // the original twin(/virtual-device)-model
    // to be used by some components for additional requesting data
    twinModel: {
      type: Object,
      required: false,
      default: () => ({})
    },
    // eslint-disable-next-line vue/require-prop-types
    aggregatedStateValue: {
      required: false,
      default: () => undefined
    },
    templateContext: {
      type: Boolean,
      required: false,
      default: false
    },
    deviceTemplate: {
      type: Object,
      required: false,
      default: () => null
    }
  },
  data: function () {
    return {
      formIsValid: true,
      transformer: {
        template: null,
        templates: {
          simple: `module.exports = (data) => {
  return data;
}`,
          wh_to_kwh: `module.exports = (data) => {
  return data / 1000;
}`,
          c_to_f: `module.exports = (celsius) => {
  return celsius * 9/5 + 32;
}`,
          f_to_c: `module.exports = (fahrenheit) => {
  return (fahrenheit - 32) * 5/9;
}`,
          battery_v_to_p: `module.exports = (voltage) => {
  // your milage may vary, this is just a linear approximation for high=100% and low=0%
  // the actual battery-percentage is probably different
  const high = 5.0; const low = 3.2;
  return Math.min(100, ((voltage- low) / (high - low)) * 100);
}`,
          i_to_b: `module.exports = (data) => {
  if (data == 0) {
    return false;
  }
  return true;
}`,
          aggregate_first_value: `module.exports = (values) => {
  const firstValue = values[Object.keys(values)[0]];
  // states that have the same key are grouped
  if (_.isArray(firstValue)) {
    return firstValue[0].value
  } else {
	  return firstValue.value
  }
}`,
          aggregate_sum: `module.exports = (values) => {
  let sum = 0;
  Object.keys(values).forEach(key => {
    const value = values[key];
    // states that have the same key are grouped
    if (_.isArray(value)) {
      value.forEach(nestedValue => {
        sum += _.get(nestedValue, 'value', nestedValue);
      });
    } else {
      // or there is just one state with this key
      sum += value.value;
    }
  });
  return sum;
}`,
          aggregate_average: `module.exports = (values) => {
  let sum = 0; let count = 0;
  Object.keys(values).forEach(key => {
    const value = values[key];
    // states that have the same key are grouped
    if (_.isArray(value)) {
      value.forEach(nestedValue => {
        sum += _.get(nestedValue, 'value', nestedValue);
        count++;
      });
    } else {
      // or there is just one state with this key
      sum += value.value;
      count++;
    }
  });
  return sum / count;
}`,
          aggregate_max: `module.exports = (values) => {
  let max = [];
  Object.keys(values).forEach(key => {
    const value = values[key];
    // states that have the same key are grouped
    if (_.isArray(value)) {
      value.forEach(nestedValue => {
        max.push(_.get(nestedValue, 'value', nestedValue));
      });
    } else {
      // or there is just one state with this key
      max.push(value.value);
    }
  });
  return _.max(max);
}`,
          aggregate_min: `module.exports = (values) => {
  let min = [];
  Object.keys(values).forEach(key => {
    const value = values[key];
    // states that have the same key are grouped
    if (_.isArray(value)) {
      value.forEach(nestedValue => {
        min.push(_.get(nestedValue, 'value', nestedValue));
      });
    } else {
      // or there is just one state with this key
      min.push(value.value);
    }
  });
  return _.min(min);
}`,
          sum_day: `module.exports = (data, lastData) => {
  // lastData = {value: 'X', time: '2020-13-37T42:99:00'} || undefined
  // check that the last value is from the same day-in-month
  const currentDate = new Date(new Date().toLocaleString('en-US', {timeZone: 'Europe/Berlin'}));
  if (_.get(lastData, 'time') && currentDate.getDate() === new Date(lastData.time).getDate()) {
    return data + (parseFloat(_.get(lastData, 'value')) || 0);
  }
  // otherwise start a new sum for today
  return data;
}`,
          sum_month: `module.exports = (data, lastData) => {
  const resetDay = 1; // reset value on every 1st of the month

  // lastData = {value: 'X', time: '2020-13-37T42:99:00'} || undefined
  if (_.get(lastData, 'time')) {
  	const priorDay = new Date(_.get(lastData, 'time')).getDate();
  	const currentDate = new Date(new Date().toLocaleString('en-US', {timeZone: 'Europe/Berlin'}));
  	if (priorDay > currentDate.getDate() || (priorDay < resetDay && currentDate.getDate() >= resetDay)) {
    	// the reset-day has passed since the last update, so start a new sum now
    	return data;
    }
  }
  return data + (parseFloat(_.get(lastData, 'value')) || 0);
}`,
          ignore_older_data: `module.exports = (data, lastData, meta) => {
  // lastData = {value: 'X', time: '2020-13-37T42:99:00'} || undefined
  if (_.get(lastData, 'time')) {
  	const priorDataDate = new Date(_.get(lastData, 'time'));
    if (priorDataDate.getTime() >= new Date(meta.timestamp).getTime()) {
      throw new Error('Ignore older data');
    }
  }
  return data;
}`
        }
      },
      changedTransformer: false,
      transformerValidRule: [() => !this.transformer.syntaxError || 'The transformer seems to be erroneous.']
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'UiTheme', 'permissions']),
    bindingType() {
      return get(this.lclModel, 'properties.binding.type');
    },
    stateValue() {
      return get(this.lclModel, 'value.value');
    },
    stateValueType() {
      return get(this.lclModel, 'properties.valueType');
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    isCreatedByTemplate() {
      return !!this.deviceTemplate;
    },
    currentScript() {
      return get(this.lclModel, 'properties.binding.transformer.script');
    }
  },
  created() {
    if (!this.currentScript) {
      const clonedLclModel = cloneDeep(this.lclModel);
      clonedLclModel.properties.binding = Object.assign({}, this.lclModel.properties.binding, {
        transformer: {
          scriptId: null,
          // UUID
          scriptName: null,
          // UUID
          script: this.transformer.templates.simple
        }
      });
      this.$emit('update:lclModel', clonedLclModel);
    }
  },
  mounted() {
    if (!this.templateContext && this.isCreatedByTemplate) {
      const currentStateKey = this.lclModel.key;
      const matchingTemplateStateDef = this.deviceTemplate.stateDefinition.find(sd => sd.key === currentStateKey);
      const clonedLclModel = cloneDeep(this.lclModel);
      set(clonedLclModel, 'properties.binding.transformer.script', get(matchingTemplateStateDef, 'properties.binding.transformer.script', ''));
      this.$emit('update:lclModel', clonedLclModel);
    }
  },
  methods: {
    onScriptChange(newScript) {
      const clonedLclModel = cloneDeep(this.lclModel);
      set(clonedLclModel, 'properties.binding.transformer.script', newScript);
      this.$emit('update:lclModel', clonedLclModel);
    },
    validateForm() {
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.formIsValid = this.$refs.form.validate();
          this.$emit('validatedTransformer');
        }
      });
    },
    refreshForm() {
      setTimeout(() => {
        // required workaround to get the cursor appearing at the right spot
        // https://discuss.codemirror.net/t/codemirror-cursor-is-not-partially-visible/648/6
        this.$refs.codemirror.codemirror.refresh();
        this.$refs.codemirror.codemirror.focus();
      }, 50);
    },
    selectedTransformerTemplate() {
      const clonedLclModel = cloneDeep(this.lclModel);
      set(clonedLclModel, 'properties.binding.transformer.script', this.transformer.templates[this.transformer.template]);
      this.$emit('update:lclModel', clonedLclModel);
    },
    saveState() {
      if (get(this.lclModel.properties.binding.transformer, 'script')) {
        // ignore empty scripts, as those only lead to errors
        if (this.currentScript.trim() === '') {
          const clonedLclModel = cloneDeep(this.lclModel);
          set(clonedLclModel, 'properties.binding.transformer.script', null);
          this.$emit('update:lclModel', clonedLclModel);
        }
      }
    }
  }
};